/**
 * Ghost Supply ROI Dashboard — CFO View
 * Tracks supply waste prevented, nursing hours recovered, and top offender procedures.
 */

import { useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Progress } from '@/components/ui/progress';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Legend,
} from 'recharts';
import {
  DollarSign,
  Clock,
  TrendingDown,
  AlertTriangle,
  Package,
  Target,
  Activity,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { getAggregateStats, MONTHLY_LEAKAGE_DATA } from '@/lib/supply/ghostSupplyData';

// Custom gauge using a half-donut
function GaugeChart({ value, max, label }: { value: number; max: number; label: string }) {
  const percent = Math.min((value / max) * 100, 100);
  const gaugeData = [
    { name: 'Prevented', value: percent },
    { name: 'Remaining', value: 100 - percent },
  ];

  return (
    <div className="flex flex-col items-center">
      <div className="w-48 h-28 relative">
        <ResponsiveContainer width="100%" height={120}>
          <PieChart>
            <Pie
              data={gaugeData}
              cx="50%"
              cy="100%"
              startAngle={180}
              endAngle={0}
              innerRadius={55}
              outerRadius={80}
              paddingAngle={2}
              dataKey="value"
              stroke="none"
            >
              <Cell fill="hsl(var(--primary))" />
              <Cell fill="hsl(var(--muted))" />
            </Pie>
          </PieChart>
        </ResponsiveContainer>
        <div className="absolute inset-0 flex flex-col items-center justify-end pb-1">
          <span className="text-2xl font-bold">${(value / 1000).toFixed(1)}K</span>
          <span className="text-[10px] text-muted-foreground">{label}</span>
        </div>
      </div>
    </div>
  );
}

export function GhostSupplyDashboard() {
  const stats = useMemo(() => getAggregateStats(), []);

  const nursingHours = (stats.totalNursingMinutes / 60).toFixed(1);

  // Bar chart data for nursing hours recovered
  const barData = MONTHLY_LEAKAGE_DATA.map(d => ({
    month: d.month,
    hours: d.nursingHoursRecovered,
    savings: d.leakagePrevented,
  }));

  return (
    <div className="space-y-6">
      {/* KPI Row */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="border-border">
          <CardContent className="pt-4 pb-3 px-4">
            <div className="flex items-center gap-2 mb-1">
              <DollarSign className="w-4 h-4 text-primary" />
              <span className="text-xs text-muted-foreground">Supply Leakage Prevented</span>
            </div>
            <p className="text-2xl font-bold">${stats.totalWasteCost.toLocaleString()}</p>
            <p className="text-[10px] text-success mt-0.5">↑ Across {stats.totalCases} cases</p>
          </CardContent>
        </Card>
        <Card className="border-border">
          <CardContent className="pt-4 pb-3 px-4">
            <div className="flex items-center gap-2 mb-1">
              <Clock className="w-4 h-4 text-primary" />
              <span className="text-xs text-muted-foreground">Nursing Hours Recovered</span>
            </div>
            <p className="text-2xl font-bold">{nursingHours}h</p>
            <p className="text-[10px] text-muted-foreground mt-0.5">{stats.totalNursingMinutes} minutes total</p>
          </CardContent>
        </Card>
        <Card className="border-border">
          <CardContent className="pt-4 pb-3 px-4">
            <div className="flex items-center gap-2 mb-1">
              <Package className="w-4 h-4 text-destructive" />
              <span className="text-xs text-muted-foreground">Ghost Items Flagged</span>
            </div>
            <p className="text-2xl font-bold text-destructive">{stats.totalWasteItems}</p>
            <p className="text-[10px] text-muted-foreground mt-0.5">Items pulled but never used</p>
          </CardContent>
        </Card>
        <Card className="border-border">
          <CardContent className="pt-4 pb-3 px-4">
            <div className="flex items-center gap-2 mb-1">
              <Target className="w-4 h-4 text-primary" />
              <span className="text-xs text-muted-foreground">Avg Card Match Rate</span>
            </div>
            <p className="text-2xl font-bold">{stats.avgMatchRate}%</p>
            <Progress value={stats.avgMatchRate} className="h-1.5 mt-1" />
          </CardContent>
        </Card>
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Gauge: Total Supply Leakage Prevented */}
        <Card className="border-border">
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <TrendingDown className="w-4 h-4 text-primary" />
              Total Supply Leakage Prevented
            </CardTitle>
            <CardDescription className="text-xs">
              Dollar value of ghost items identified and removed from preference cards
            </CardDescription>
          </CardHeader>
          <Separator />
          <CardContent className="pt-4 flex justify-center">
            <GaugeChart
              value={stats.totalWasteCost}
              max={120000}
              label="of $120K annual target"
            />
          </CardContent>
          <CardContent className="pt-0 pb-4">
            <div className="grid grid-cols-3 gap-2 text-center">
              <div className="bg-muted/50 rounded-lg p-2">
                <p className="text-lg font-bold">{stats.totalCases}</p>
                <p className="text-[10px] text-muted-foreground">Cases Analyzed</p>
              </div>
              <div className="bg-muted/50 rounded-lg p-2">
                <p className="text-lg font-bold">{stats.totalWasteItems}</p>
                <p className="text-[10px] text-muted-foreground">Ghost Items</p>
              </div>
              <div className="bg-muted/50 rounded-lg p-2">
                <p className="text-lg font-bold">${Math.round(stats.totalWasteCost / stats.totalWasteItems)}</p>
                <p className="text-[10px] text-muted-foreground">Avg Item Cost</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Bar Chart: Nursing Hours Recovered */}
        <Card className="border-border">
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <Activity className="w-4 h-4 text-primary" />
              Nursing Hours Recovered
            </CardTitle>
            <CardDescription className="text-xs">
              Monthly restock time eliminated by preference card synchronization
            </CardDescription>
          </CardHeader>
          <Separator />
          <CardContent className="pt-4">
            <div className="h-[220px]">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={barData} margin={{ top: 5, right: 10, left: -10, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                  <XAxis
                    dataKey="month"
                    tick={{ fill: 'hsl(var(--muted-foreground))', fontSize: 11 }}
                    axisLine={{ stroke: 'hsl(var(--border))' }}
                  />
                  <YAxis
                    tick={{ fill: 'hsl(var(--muted-foreground))', fontSize: 11 }}
                    axisLine={{ stroke: 'hsl(var(--border))' }}
                    unit="h"
                  />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: 'hsl(var(--card))',
                      border: '1px solid hsl(var(--border))',
                      borderRadius: '8px',
                      fontSize: 12,
                    }}
                    formatter={(value: number, name: string) =>
                      name === 'hours' ? [`${value}h`, 'Hours Recovered'] : [`$${value.toLocaleString()}`, 'Savings']
                    }
                  />
                  <Bar dataKey="hours" fill="hsl(var(--primary))" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Top Offenders List */}
      <Card className="border-border">
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <AlertTriangle className="w-4 h-4 text-warning" />
            Top Offenders — Card Cleansing Priority
          </CardTitle>
          <CardDescription className="text-xs">
            Procedures with the highest supply/dictation mismatch. Target these for preference card audits.
          </CardDescription>
        </CardHeader>
        <Separator />
        <CardContent className="pt-3 space-y-2">
          {stats.topOffenders.map((proc, idx) => (
            <div
              key={proc.id}
              className="flex items-center gap-3 px-3 py-3 rounded-lg border border-border bg-card hover:bg-muted/30 transition-colors"
            >
              {/* Rank */}
              <div className={cn(
                'w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold shrink-0',
                idx === 0 && 'bg-destructive/20 text-destructive',
                idx === 1 && 'bg-warning/20 text-warning',
                idx >= 2 && 'bg-muted text-muted-foreground',
              )}>
                #{idx + 1}
              </div>

              {/* Info */}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-semibold truncate">{proc.procedureName}</span>
                  <Badge variant="outline" className="text-[10px] shrink-0 border-border">
                    CPT {proc.cptCode}
                  </Badge>
                </div>
                <div className="flex items-center gap-3 mt-1">
                  <span className="text-xs text-muted-foreground">{proc.totalCases} cases</span>
                  <span className="text-xs text-destructive font-medium">{proc.wasteItems} ghost items</span>
                  <span className="text-xs text-warning">{proc.adhocItems} ad-hoc</span>
                </div>
              </div>

              {/* Metrics */}
              <div className="text-right shrink-0">
                <p className="text-sm font-bold">${proc.estimatedWasteCost.toLocaleString()}</p>
                <p className="text-[10px] text-muted-foreground">waste cost</p>
              </div>

              {/* Match rate bar */}
              <div className="w-16 shrink-0">
                <div className="flex items-center justify-between mb-0.5">
                  <span className="text-[10px] text-muted-foreground">Match</span>
                  <span className={cn(
                    'text-[10px] font-medium',
                    proc.matchRate >= 80 ? 'text-success' : proc.matchRate >= 70 ? 'text-warning' : 'text-destructive',
                  )}>{proc.matchRate}%</span>
                </div>
                <Progress
                  value={proc.matchRate}
                  className={cn('h-1.5')}
                />
              </div>
            </div>
          ))}
        </CardContent>
      </Card>
    </div>
  );
}
